export default function Services() {
  const services = [
    {
      icon: 'ri-layout-line',
      title: 'Custom Website Design',
      description: 'Unique, professional websites tailored to your brand. Mobile-responsive designs that make your business stand out online.',
      features: ['Responsive Design', 'Brand Integration', 'Modern UI/UX', 'Fast Loading']
    },
    {
      icon: 'ri-shopping-cart-line',
      title: 'E-commerce Solutions',
      description: 'Complete online stores for small and medium businesses. Sell products with secure payment processing and inventory management.',
      features: ['Product Catalog', 'Shopping Cart', 'Payment Gateway', 'Order Tracking']
    },
    {
      icon: 'ri-search-line',
      title: 'SEO Optimization',
      description: 'Get found on Google with search engine optimization. Improve your rankings and attract more customers to your website.',
      features: ['Keyword Research', 'On-Page SEO', 'Local SEO', 'Analytics Setup']
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile-First Design',
      description: 'Websites that look perfect on all devices. Optimized for smartphones, tablets, and desktops to reach customers everywhere.',
      features: ['Responsive Layout', 'Touch Friendly', 'Fast Mobile Speed', 'App-Like Feel']
    },
    {
      icon: 'ri-pencil-ruler-2-line',
      title: 'Landing Pages',
      description: 'High-converting landing pages for campaigns and promotions. Perfect for lead generation and product launches.',
      features: ['Lead Capture Forms', 'Call-to-Action', 'A/B Testing Ready', 'Conversion Focused']
    },
    {
      icon: 'ri-tools-line',
      title: 'Website Maintenance',
      description: 'Keep your website running smoothly with regular updates, backups, and technical support. Focus on your business, we handle the tech.',
      features: ['Regular Updates', 'Security Monitoring', 'Backup Service', 'Technical Support']
    },
    {
      icon: 'ri-store-2-line',
      title: 'Business Websites',
      description: 'Professional websites for local businesses and service providers. Showcase your services and connect with customers online.',
      features: ['Contact Forms', 'Google Maps', 'Service Pages', 'Testimonials']
    },
    {
      icon: 'ri-refresh-line',
      title: 'Website Redesign',
      description: 'Modernize your outdated website with a fresh look and improved functionality. Better design, better user experience, better results.',
      features: ['Modern Design', 'Improved UX', 'Speed Boost', 'Mobile Optimization']
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-cyan-100 text-cyan-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
            <i className="ri-service-line"></i>
            Our Services
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight px-4">
            Website Design Services for{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Small &amp; Medium Businesses
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Professional web design solutions to help your business grow online. From custom websites to e-commerce stores, we've got you covered.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2"
            >
              {/* Icon */}
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                <i className={`${service.icon} text-white text-2xl sm:text-3xl`}></i>
              </div>

              {/* Content */}
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                {service.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2 sm:space-y-3">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 shadow-2xl">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Ready to Grow Your Business Online?
          </h3>
          <p className="text-base sm:text-lg lg:text-xl text-cyan-50 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Get a free consultation and discover how a professional website can transform your business. No obligation, just expert advice.
          </p>
          <button 
            onClick={scrollToContact}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-cyan-600 text-sm sm:text-base font-semibold rounded-full hover:bg-gray-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            Get Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
}
